import { ACTIVITY_THROTTLE_MS } from './constants';

/**
 * ActivityTracker listens for user interaction events and calls a throttled
 * callback so the session manager can update `lastActivityAt`.
 *
 * Events are throttled — at most one callback per ACTIVITY_THROTTLE_MS.
 */
export class ActivityTracker {
  readonly #onActivity: () => void;
  readonly #events: readonly string[] = ['click', 'scroll', 'keydown', 'touchstart'];
  #lastFired = 0;
  #bound: (() => void) | null = null;

  constructor(onActivity: () => void) {
    this.#onActivity = onActivity;
  }

  start(): void {
    if (this.#bound) return; // already running
    this.#bound = this.#handleEvent.bind(this);
    for (const event of this.#events) {
      document.addEventListener(event, this.#bound, { passive: true, capture: true });
    }
  }

  stop(): void {
    if (!this.#bound) return;
    for (const event of this.#events) {
      document.removeEventListener(event, this.#bound, { capture: true });
    }
    this.#bound = null;
  }

  #handleEvent(): void {
    const now = Date.now();
    if (now - this.#lastFired < ACTIVITY_THROTTLE_MS) return;
    this.#lastFired = now;
    this.#onActivity();
  }
}
